import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, CheckCircle, Camera, PencilSimple, X, Timer,
  ForkKnife, Fire, Package, Warning
} from "@phosphor-icons/react";

const STAGE_ICON = { Preparation: ForkKnife, Cooking: Fire };

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";
function photoUrl(path) {
  if (!path) return null;
  const token = localStorage.getItem("chericious_token") || "";
  return `${API_BASE}/files/${path}?auth=${encodeURIComponent(token)}`;
}

/**
 * Full-screen chef stepper — one instruction at a time. Optimised for tablets
 * and phones held vertically on the kitchen line: large touch targets,
 * high-contrast typography, sticky action bar at the bottom.
 */
export default function KitchenStepper({ batch, open, onOpenChange, onComplete }) {
  const [sop, setSop] = useState(null);
  const [checklist, setChecklist] = useState({ preparation: [], cooking: [] });
  const [cursor, setCursor] = useState(0);
  const [busy, setBusy] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteOpen, setNoteOpen] = useState(false);
  const [photoLightbox, setPhotoLightbox] = useState(null);

  const stageKey = batch?.stage?.toLowerCase();
  const steps = sop?.[stageKey] || [];
  const step = steps[cursor];
  const ticksByStep = useMemo(() => {
    const m = {};
    (checklist[stageKey] || []).forEach(c => { m[c.step_no] = c; });
    return m;
  }, [checklist, stageKey]);
  const doneCount = steps.filter(s => ticksByStep[s.step_no]).length;
  const total = steps.length;
  const pct = total ? Math.round((doneCount / total) * 100) : 0;
  const allDone = total > 0 && doneCount >= total;
  const currentTick = step ? ticksByStep[step.step_no] : null;

  const load = useCallback(async () => {
    if (!batch?.id) return;
    try {
      const { data } = await api.get(`/batches/${batch.id}/sop`);
      setSop(data.sop);
      setChecklist(data.checklist);
      // jump cursor to the first unticked step of the current stage
      const list = data.sop?.[batch.stage.toLowerCase()] || [];
      const ticks = new Set((data.checklist?.[batch.stage.toLowerCase()] || []).map(c => c.step_no));
      const firstOpen = list.findIndex(s => !ticks.has(s.step_no));
      setCursor(firstOpen >= 0 ? firstOpen : Math.max(0, list.length - 1));
    } catch {}
  }, [batch]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const StageIcon = STAGE_ICON[batch?.stage] || ForkKnife;

  const goPrev = () => setCursor(c => Math.max(0, c - 1));
  const goNext = () => setCursor(c => Math.min(steps.length - 1, c + 1));

  const tickStep = async (advanceCursor = true) => {
    if (!step || busy) return;
    // photo required guard
    if (step.photo_required && !currentTick?.photo_path) {
      toast.error("Attach a photo before marking this step done.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/batches/${batch.id}/sop-check`, {
        stage: stageKey, step_no: step.step_no,
        chef_note: noteText || currentTick?.chef_note || "",
      });
      setNoteText("");
      await load();
      if (advanceCursor && cursor < steps.length - 1) setCursor(c => c + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not mark done");
    } finally { setBusy(false); }
  };

  const untick = async () => {
    if (!step || busy || !currentTick) return;
    setBusy(true);
    try {
      await api.post(`/batches/${batch.id}/sop-uncheck`, {
        stage: stageKey, step_no: step.step_no,
      });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not un-tick"); }
    finally { setBusy(false); }
  };

  const uploadPhoto = async (file) => {
    if (!file || !step || busy) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const params = new URLSearchParams({
        batch_id: batch.id, stage: stageKey, step_no: String(step.step_no),
      });
      const { data } = await api.post(`/uploads/sop-photo?${params.toString()}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Persist tick with photo (creates or updates)
      await api.post(`/batches/${batch.id}/sop-check`, {
        stage: stageKey, step_no: step.step_no, photo_path: data.storage_path,
        chef_note: currentTick?.chef_note || "",
      });
      toast.success("Photo attached · step marked done");
      await load();
      if (cursor < steps.length - 1) setCursor(c => c + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Photo upload failed"); }
    finally { setBusy(false); }
  };

  const completeStage = async () => {
    setBusy(true);
    try {
      await api.post(`/batches/${batch.id}/advance`, {});
      toast.success(`Batch ${batch.batch_no} advanced to next stage`);
      onOpenChange(false);
      onComplete && onComplete();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Could not advance"));
    } finally { setBusy(false); }
  };

  if (!batch || !open) return null;
  if (!sop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="p-8"><DialogTitle className="sr-only">Loading</DialogTitle><DialogDescription className="sr-only">Loading</DialogDescription>
          <div className="text-muted-foreground text-center py-10">Loading recipe…</div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="p-0 gap-0 max-w-2xl w-[100vw] sm:w-full h-[100dvh] sm:h-auto sm:max-h-[92vh] rounded-none sm:rounded-lg overflow-hidden flex flex-col"
        data-testid="kitchen-stepper"
      >
        <DialogTitle className="sr-only">SOP stepper for {batch.batch_no}</DialogTitle>
        <DialogDescription className="sr-only">Step-by-step cooking guide</DialogDescription>

        {/* Header */}
        <div className="px-5 py-4 border-b border-border bg-card sticky top-0 z-10">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${batch.stage === "Preparation" ? "bg-sky-100 text-sky-700" : "bg-orange-100 text-orange-700"}`}>
                <StageIcon size={22} weight="duotone"/>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="mono text-xs font-bold">{batch.batch_no}</span>
                  <Badge variant="secondary" className="text-[10px]">{batch.stage}</Badge>
                </div>
                <div className="font-display font-bold text-base sm:text-lg leading-tight truncate">{batch.sku_name}</div>
              </div>
            </div>
            <button onClick={() => onOpenChange(false)}
                    className="w-11 h-11 -mr-2 flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground"
                    data-testid="stepper-close" aria-label="Close">
              <X size={22}/>
            </button>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <Progress value={pct} className="h-2"/>
            </div>
            <div className="mono text-sm font-bold tabular-nums whitespace-nowrap">
              <span className={allDone ? "text-emerald-600" : ""}>{doneCount}</span>
              <span className="text-muted-foreground">/{total}</span>
            </div>
          </div>
        </div>

        {/* Step body */}
        {step ? (
          <div className="flex-1 overflow-y-auto px-5 py-6 sm:py-8">
            <div className="max-w-xl mx-auto">
              <div className="mono text-xs font-bold text-muted-foreground tracking-[0.2em] mb-3">
                STEP {step.step_no} OF {total}
              </div>
              <h2 className="font-display font-black text-3xl sm:text-4xl leading-[1.05] tracking-tight text-foreground mb-3"
                  data-testid={`stepper-title-${step.step_no}`}>
                {step.title}
              </h2>
              <div className="flex items-center gap-2 mb-5 text-sm">
                <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 mono text-xs font-semibold">
                  <Timer size={13}/>{step.duration_min} min
                </span>
                {step.photo_required && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2.5 py-1 text-xs font-bold uppercase tracking-wide">
                    <Camera size={13} weight="fill"/>Photo required
                  </span>
                )}
                {currentTick && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-1 text-xs font-bold">
                    <CheckCircle size={13} weight="fill"/>Done · {new Date(currentTick.checked_at).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})}
                  </span>
                )}
              </div>

              {step.description && (
                <p className="text-base sm:text-lg text-foreground/85 leading-relaxed mb-6">
                  {step.description}
                </p>
              )}

              {(step.ingredients || []).length > 0 && (
                <div className="mb-6">
                  <div className="overline mb-2">Ingredients for this step</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {step.ingredients.map((i, k) => (
                      <div key={k} className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2.5">
                        <span className="text-sm font-medium">{i.name}</span>
                        <span className="mono text-sm font-bold">{i.qty}<span className="text-muted-foreground text-xs">{i.unit}</span></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Photo section */}
              {(step.photo_required || currentTick?.photo_path) && (
                <div className="mb-6">
                  <div className="overline mb-2">Photo evidence</div>
                  {currentTick?.photo_path ? (
                    <button onClick={() => setPhotoLightbox(currentTick.photo_path)}
                            className="block rounded-lg overflow-hidden border-2 border-emerald-200 hover:border-primary"
                            data-testid="stepper-photo-thumb">
                      <img src={photoUrl(currentTick.photo_path)} alt="SOP evidence"
                           className="w-full max-w-xs h-40 object-cover"/>
                    </button>
                  ) : (
                    <label className="flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50 text-amber-800 h-24 cursor-pointer active:bg-amber-100 font-semibold"
                           data-testid="stepper-photo-upload-label">
                      <Camera size={22}/>Tap to attach photo
                      <input type="file" accept="image/*" capture="environment" className="hidden"
                             onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])}
                             data-testid="stepper-photo-upload"/>
                    </label>
                  )}
                </div>
              )}

              {/* Chef note preview / edit */}
              <div className="mb-6">
                <div className="overline mb-2">Chef note (optional)</div>
                {noteOpen ? (
                  <div className="flex gap-2">
                    <Input value={noteText} onChange={e => setNoteText(e.target.value)}
                           placeholder="Anything QC / next shift should know"
                           className="h-11 text-base"
                           data-testid="stepper-note-input"/>
                    <Button variant="outline" onClick={() => { setNoteOpen(false); setNoteText(""); }} className="h-11">Cancel</Button>
                  </div>
                ) : (
                  <button onClick={() => { setNoteText(currentTick?.chef_note || ""); setNoteOpen(true); }}
                          className="flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
                          data-testid="stepper-note-open">
                    <PencilSimple size={15}/>
                    {currentTick?.chef_note ? `"${currentTick.chef_note}"` : "Add a note"}
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-5 py-10 text-center">
            <Warning size={36} className="text-amber-500 mx-auto mb-2"/>
            <div className="font-semibold text-lg">No SOP defined for {batch.stage}</div>
            <div className="text-sm text-muted-foreground mt-1">Ask an admin to add SOP steps in <span className="mono">SKUs → SOP</span>.</div>
          </div>
        )}

        {/* Sticky bottom action bar */}
        <div className="border-t border-border bg-card px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sticky bottom-0">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="lg" className="h-14 w-14 shrink-0 p-0"
                    onClick={goPrev} disabled={cursor === 0 || busy}
                    data-testid="stepper-prev" aria-label="Previous step">
              <ArrowLeft size={22}/>
            </Button>

            {allDone ? (
              <Button size="lg" className="flex-1 h-14 text-base font-bold"
                      onClick={completeStage} disabled={busy}
                      data-testid="stepper-complete-stage">
                Complete {batch.stage}
                <Package size={18} className="ml-2"/>
              </Button>
            ) : currentTick ? (
              <div className="flex-1 flex items-center gap-2">
                <Button variant="outline" size="lg"
                        className="h-14 flex-1 text-base font-semibold border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                        onClick={untick} disabled={busy}
                        data-testid="stepper-untick">
                  <CheckCircle size={20} weight="fill" className="mr-1.5"/>Done · un-tick
                </Button>
                <Button size="lg" className="h-14 text-base font-bold px-6"
                        onClick={goNext} disabled={cursor >= steps.length - 1 || busy}
                        data-testid="stepper-next">
                  Next <ArrowRight size={20} className="ml-1.5"/>
                </Button>
              </div>
            ) : (
              <Button size="lg" className="flex-1 h-14 text-base font-bold"
                      onClick={() => tickStep(true)}
                      disabled={busy || (step?.photo_required && !currentTick?.photo_path)}
                      data-testid="stepper-mark-done">
                <CheckCircle size={22} weight="bold" className="mr-2"/>
                Mark done{cursor < steps.length - 1 ? " → Next" : ""}
              </Button>
            )}

            {allDone ? null : (
              <Button variant="outline" size="lg" className="h-14 w-14 shrink-0 p-0"
                      onClick={goNext} disabled={cursor >= steps.length - 1 || busy}
                      data-testid="stepper-next-arrow" aria-label="Next step">
                <ArrowRight size={22}/>
              </Button>
            )}
          </div>
          {noteOpen && (
            <Button size="sm" className="w-full mt-2" onClick={() => tickStep(false)} disabled={busy}
                    data-testid="stepper-save-note-tick">
              Save note {currentTick ? "" : "& mark done"}
            </Button>
          )}
        </div>

        {/* Photo lightbox */}
        <Dialog open={!!photoLightbox} onOpenChange={(o) => !o && setPhotoLightbox(null)}>
          <DialogContent className="max-w-3xl">
            <DialogTitle>SOP evidence photo</DialogTitle>
            <DialogDescription>Stored for QC traceability.</DialogDescription>
            {photoLightbox && <img src={photoUrl(photoLightbox)} alt="SOP evidence"
                                   className="w-full max-h-[70vh] object-contain rounded"/>}
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
